import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { Header } from "@/components/Header";
import { ProgressIndicator } from "@/components/ProgressIndicator";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Upload, Link as LinkIcon, ArrowLeft, ArrowRight, X, Type, Loader2, Info, Eye } from "lucide-react";
import { useTest } from "@/context/TestContext";
import type { TestConfig, ContentSegment } from "@shared/schema";
import { ACCEPTED_FILE_EXTENSIONS, MAX_EXTRACTED_CHARS } from "@shared/schema";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MaterialPreviewDialog } from "@/components/MaterialPreviewDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { SubjectSelector } from "@/components/SubjectSelector";
import { useToast } from "@/hooks/use-toast";

const MAX_UPLOAD_SIZE = 25 * 1024 * 1024; // 25MB, matches backend
const ACCEPT_ATTR = ACCEPTED_FILE_EXTENSIONS.join(",");
// Soft cap during early days. Keeps the AI prompt size manageable, the
// localStorage footprint sane, and disk usage on the server bounded.
const MAX_MATERIALS = 4;
// How long a URL accessibility result stays trusted before we re-probe.
// Long enough to make refreshes cheap; short enough that a teacher who
// toggled sharing settings won't be confused for too long.
const URL_STATUS_TTL_MS = 60 * 60 * 1000; // 1 hour
export const SOURCE_BANNER_KEY = 'testcraft.sourceMaterialsBannerDismissed';
// One-time flag for clearing the old "10" / "math" auto-defaults that used
// to be persisted by previous versions. Once cleared, the user picks fresh.
const REQUIRED_RESET_KEY = 'testcraft.requiredFieldsReset.v1';

// Map a filename to its display tag (PDF / DOCX / PPTX / TXT / FILE).
// We derive from extension rather than mimetype because the mimetype isn't
// always preserved through the upload round-trip.
const fileTypeLabel = (filename: string | undefined): string => {
  if (!filename) return 'FILE';
  const ext = filename.toLowerCase().split('.').pop() || '';
  switch (ext) {
    case 'pdf': return 'PDF';
    case 'docx': return 'DOCX';
    case 'pptx': return 'PPTX';
    case 'txt': return 'TXT';
    default: return 'FILE';
  }
};

// Pull a recognizable site name out of a URL. We strip protocol + www and
// return the hostname (e.g. "khanacademy.org"). Falls back to the raw string
// if it isn't a parseable URL.
const urlSiteLabel = (raw: string): string => {
  if (!raw) return '';
  try {
    const candidate = raw.startsWith('http') ? raw : `https://${raw}`;
    return new URL(candidate).hostname.replace(/^www\./, '');
  } catch {
    return raw;
  }
};

// Google Slides/Docs (and Drive doc/slide) links are JS-rendered or login-
// gated, so the AI can't read their content for blueprinting. We flag these
// with a warning rather than silently letting them contribute nothing.
const isGoogleDocSlidesUrl = (raw: string): boolean => {
  if (!raw) return false;
  try {
    const u = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'docs.google.com') return /\/(presentation|document)\b/.test(u.pathname);
    if (host === 'slides.google.com') return true;
    if (host === 'drive.google.com') return true;
    return false;
  } catch {
    return false;
  }
};

type MaterialItem = {
  id: string;
  type: 'file' | 'text' | 'url';
  content: string;
  name: string;
  size?: number;
  truncated?: boolean;
  originalLength?: number;
  mimeType?: string;
  // SHA-256 of the original file bytes. Used for stronger dedup (catches
  // identical content with different filenames). Only set for files.
  hash?: string;
  // Per-page / per-slide / per-section breakdown of the file's content,
  // each carrying its source anchor. Used downstream by BuildBlueprint to
  // tag the materials text sent to the AI so we can derive Content
  // Alignment tooltips per topic.
  segments?: ContentSegment[];
  // Opaque server-side id from POST /api/upload. Used by the preview
  // dialog to fetch the original file bytes from /api/uploads/:uploadId.
  uploadId?: string;
  // For url materials only: result of the server-side accessibility
  // probe. "checking" while the request is in flight; undefined for
  // non-URL materials. Persisted along with urlStatusCheckedAt so we
  // can skip re-probing on mount when the cached result is still
  // fresh (URL_STATUS_TTL_MS).
  urlStatus?: 'checking' | 'public' | 'login_required' | 'unreachable' | 'unsupported_provider';
  urlStatusCheckedAt?: number; // ms epoch
};

async function sha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export default function IdentifyContent() {
  const [, setLocation] = useLocation();
  const { config, setConfig, testName, setTestName, blueprint, setBlueprint } = useTest();
  const { toast } = useToast();

  const materialFileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingMaterials, setIsDraggingMaterials] = useState(false);
  const [isUploadingMaterials, setIsUploadingMaterials] = useState(false);

  // Start empty when the name is just the default, so "Untitled Test" shows as
  // a vanishing placeholder rather than a pre-filled value the user must clear.
  const [localTestName, setLocalTestName] = useState(
    testName && testName !== "Untitled Test" ? testName : "",
  );
  const [audience, setAudience] = useState<TestConfig["audience"]>(config?.audience || "High School Students");
  // Reading level and subject default to empty so the user has to make an
  // active choice. Empty values fall through to safe downstream defaults
  // (grade 7 / general) until the user picks something.
  const [readingLevel, setReadingLevel] = useState<TestConfig["readingLevel"] | "">(config?.readingLevel || "");
  const [subject, setSubject] = useState<string>(config?.subject || "");
  const [lengthValue, setLengthValue] = useState(Math.min(config?.testLengthValue || 10, 20));
  const [materials, setMaterials] = useState<MaterialItem[]>(
    config?.materials?.map((m, i) => ({
      id: m.id || `material-${i}`,
      type: m.type === 'url' ? 'url' : m.type === 'text' ? 'text' : 'file',
      content: m.content ?? m.url ?? m.name,
      name: m.name,
      size: m.size,
      truncated: m.truncated,
      originalLength: m.originalLength,
      mimeType: m.mimetype,
      hash: m.hash,
      segments: m.segments,
      uploadId: m.uploadId,
      urlStatus: m.urlStatus,
      urlStatusCheckedAt: m.urlStatusCheckedAt,
    })) || []
  );
  const [urlInput, setUrlInput] = useState("");
  const [textInput, setTextInput] = useState("");
  const [materialType, setMaterialType] = useState<"file" | "text" | "url">("file");
  const [previewMaterial, setPreviewMaterial] = useState<MaterialItem | null>(null);

  // Validation state for the Next button. Filled in when the user presses
  // Next while any required field is empty; cleared field-by-field as the
  // user fills things in.
  const [missingFields, setMissingFields] = useState<Set<string>>(new Set());
  const nameWrapRef = useRef<HTMLDivElement>(null);
  const readingLevelWrapRef = useRef<HTMLDivElement>(null);
  const subjectWrapRef = useRef<HTMLDivElement>(null);
  const materialsWrapRef = useRef<HTMLDivElement>(null);
  const fieldLabels: Record<string, string> = {
    name: 'a test name',
    readingLevel: 'a reading level',
    subject: 'a subject area',
    materials: 'at least one lesson material',
  };
  const clearError = (field: string) =>
    setMissingFields((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  // First-run banner explaining what source materials are for. Dismissal
  // persists across sessions so returning users don't see it again.
  const [showSourceBanner, setShowSourceBanner] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem(SOURCE_BANNER_KEY) !== '1';
  });
  const dismissSourceBanner = () => {
    setShowSourceBanner(false);
    try { localStorage.setItem(SOURCE_BANNER_KEY, '1'); } catch { /* private mode */ }
  };
  // Replaces the old "Lesson Plan / Learning Objectives" upload zone. One
  // optional free-text field for whatever extra direction the teacher wants
  // to give the AI: pacing, tone, what to emphasize/avoid, accommodations.
  // Plumbed through to BuildBlueprint as a labeled section separate from
  // source materials so Claude treats it as instructions, not content.
  const [additionalContext, setAdditionalContext] = useState<string>(
    config?.additionalContext ?? "",
  );
  const [editingText, setEditingText] = useState<{id: string, content: string, isMaterial: boolean} | null>(null);

  // Ask the server whether a URL appears publicly reachable. Mutates the
  // matching material item with the result. Used both by handleAddUrl
  // (fresh adds) and by the on-mount re-check effect (stale or missing).
  // Stamps urlStatusCheckedAt on completion so the cache TTL can compare.
  const checkUrlAccessibility = async (materialId: string, url: string) => {
    try {
      const res = await fetch('/api/url/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const data = await res.json();
      const status: MaterialItem['urlStatus'] =
        data?.status === 'public' || data?.status === 'login_required' || data?.status === 'unreachable'
          ? data.status
          : 'unreachable';
      const checkedAt = Date.now();
      // If the server extracted readable page text, store it as an (anchorless)
      // segment so the link's content feeds the blueprint + assistant like a
      // file. Keep `content` as the URL itself (used for the link + save).
      const extractedText: string | undefined =
        typeof data?.extractedText === 'string' && data.extractedText.trim() ? data.extractedText : undefined;
      setMaterials((prev) => prev.map((m) => {
        if (m.id !== materialId) return m;
        const next: MaterialItem = { ...m, urlStatus: status, urlStatusCheckedAt: checkedAt };
        if (extractedText) {
          next.segments = [{ text: extractedText }];
          next.truncated = !!data.truncated;
          next.originalLength = typeof data.originalLength === 'number' ? data.originalLength : undefined;
        }
        return next;
      }));
    } catch {
      const checkedAt = Date.now();
      setMaterials((prev) => prev.map((m) => (m.id === materialId ? { ...m, urlStatus: 'unreachable', urlStatusCheckedAt: checkedAt } : m)));
    }
  };

  // True if a URL material's cached status is still trustworthy. The
  // mount effect uses this to skip re-probing.
  const isUrlStatusFresh = (m: MaterialItem): boolean => {
    if (!m.urlStatus || m.urlStatus === 'checking') return false;
    if (!m.urlStatusCheckedAt) return false;
    return Date.now() - m.urlStatusCheckedAt < URL_STATUS_TTL_MS;
  };

  // Shared cap-check: surface a toast and bail when at MAX_MATERIALS. Used
  // by all three add paths so the limit is enforced consistently.
  const enforceMaterialCap = (): boolean => {
    if (materials.length >= MAX_MATERIALS) {
      toast({
        title: 'Material limit reached',
        description: `You can add up to ${MAX_MATERIALS} lesson materials in this version. Remove one to add another.`,
        variant: 'destructive',
      });
      return false;
    }
    return true;
  };

  const handleAddUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    if (!enforceMaterialCap()) return;
    const id = `material-${Date.now()}`;
    const isGoogle = isGoogleDocSlidesUrl(trimmed);
    const newItem: MaterialItem = {
      id,
      type: 'url',
      content: trimmed,
      name: trimmed,
      urlStatus: isGoogle ? 'unsupported_provider' : 'checking',
      ...(isGoogle ? { urlStatusCheckedAt: Date.now() } : {}),
    };
    setMaterials([...materials, newItem]);
    setUrlInput("");
    // Google Slides/Docs can't be read; skip the accessibility probe (which
    // would otherwise overwrite the warning with public/unreachable).
    if (!isGoogle) void checkUrlAccessibility(id, trimmed);
  };

  const handleAddText = () => {
    if (!textInput.trim()) return;
    if (!enforceMaterialCap()) return;
    // Match the file-upload budget (server/upload.ts MAX_EXTRACTED_CHARS) so
    // text materials behave consistently — anything past the cap gets
    // truncated and surfaces the existing "Shortened" badge on the material
    // card.
    const MAX_TEXT_CHARS = MAX_EXTRACTED_CHARS;
    const truncated = textInput.length > MAX_TEXT_CHARS;
    const content = truncated ? textInput.slice(0, MAX_TEXT_CHARS) : textInput;
    const newItem = {
      id: `material-${Date.now()}`,
      type: 'text' as const,
      content,
      name: `Text content (${content.substring(0, 30)}${content.length > 30 ? '...' : ''})`,
      truncated,
      originalLength: textInput.length,
    };
    setMaterials([...materials, newItem]);
    setTextInput("");
  };

  const handleRemoveMaterial = (id: string) => {
    const removed = materials.find((m) => m.id === id);
    setMaterials(materials.filter(m => m.id !== id));
    // Best-effort cleanup of the server-side upload file. Failures here
    // are non-fatal — the periodic janitor will catch leftover files.
    if (removed?.uploadId) {
      fetch(`/api/uploads/${removed.uploadId}`, { method: 'DELETE' }).catch(() => { /* ignore */ });
    }
  };

  const handleEditMaterial = (id: string, newContent: string) => {
    setMaterials(materials.map(m =>
      m.id === id
        ? { ...m, content: newContent, name: `Text content (${newContent.substring(0, 30)}${newContent.length > 30 ? '...' : ''})` }
        : m
    ));
    setEditingText(null);
  };

  // For URL materials, the extracted page text lives in segments (content
  // stays the link). Edits replace that text; keep content/name untouched.
  const handleEditUrlContent = (id: string, newText: string) => {
    setMaterials(materials.map(m =>
      m.id === id
        ? { ...m, segments: newText.trim() ? [{ text: newText }] : [], truncated: false, originalLength: undefined }
        : m
    ));
    setEditingText(null);
  };

  const validateFile = (file: File): string | null => {
    const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (!ext || !ACCEPTED_FILE_EXTENSIONS.includes(ext as (typeof ACCEPTED_FILE_EXTENSIONS)[number])) {
      return `${file.name}: unsupported type. Allowed: ${ACCEPTED_FILE_EXTENSIONS.join(", ")}.`;
    }
    if (file.size > MAX_UPLOAD_SIZE) {
      return `${file.name}: exceeds 25MB limit (${(file.size / 1024 / 1024).toFixed(1)}MB).`;
    }
    return null;
  };

  const uploadFile = async (file: File, idPrefix: 'material' | 'lesson'): Promise<MaterialItem> => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch("/api/upload", { method: "POST", body: formData });
    // 413 (and some other proxy errors) come back as HTML from nginx, not
    // JSON — parsing it as JSON would throw a misleading error.
    if (res.status === 413) {
      throw new Error(`${file.name} is too large for the server to accept.`);
    }
    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      throw new Error(`Upload failed (${res.status}). Server returned an unexpected response.`);
    }
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || `Upload failed (${res.status})`);
    }
    // Defense-in-depth: never store an empty extraction even if the server
    // somehow returned 200 — mirrors the URL path's discard in
    // checkUrlAccessibility (L234-235). Normally the server 422s this case
    // with a cause-naming message that `!res.ok` already surfaced.
    if (!(typeof data.extractedText === 'string' && data.extractedText.trim())) {
      throw new Error(
        `No readable text was extracted from this file — it may be a scan or image. ` +
        `Text recognition isn't supported yet, so paste the text or use a text-based file instead.`
      );
    }
    return {
      id: `${idPrefix}-${data.id}`,
      type: 'file',
      content: data.extractedText,
      name: data.filename,
      size: data.size,
      truncated: data.truncated,
      originalLength: data.originalLength,
      mimeType: data.mimetype,
      uploadId: data.id,
      segments: Array.isArray(data.segments) ? data.segments : undefined,
    };
  };

  const handleFilesUpload = async (files: FileList | File[]) => {
    let fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    // Honor the material cap: accept up to the remaining slots, warn if
    // we had to drop any. If we're already at the cap, bail entirely.
    const remaining = MAX_MATERIALS - materials.length;
    if (remaining <= 0) {
      toast({
        title: 'Material limit reached',
        description: `You can add up to ${MAX_MATERIALS} lesson materials in this version. Remove one to add another.`,
        variant: 'destructive',
      });
      return;
    }
    if (fileArray.length > remaining) {
      const dropped = fileArray.length - remaining;
      fileArray = fileArray.slice(0, remaining);
      toast({
        title: 'Some files skipped',
        description: `Only ${remaining} more file${remaining === 1 ? '' : 's'} could be added (${MAX_MATERIALS} max). ${dropped} skipped.`,
        variant: 'destructive',
      });
    }

    const existing = materials;
    const setItems = setMaterials;
    const setUploading = setIsUploadingMaterials;
    const idPrefix = 'material';

    setUploading(true);

    type Prepared =
      | { ok: true; file: File; hash: string }
      | { ok: false; file: File; error: string };

    // Validate + hash all files in parallel before uploading. Hashing is
    // CPU-bound but fast (SHA-256 of 25MB ≈ 120ms on modern devices); doing
    // them concurrently masks the cost on multi-file batches.
    const prepared: Prepared[] = await Promise.all(
      fileArray.map(async (file): Promise<Prepared> => {
        const validationError = validateFile(file);
        if (validationError) {
          return { ok: false, file, error: validationError };
        }
        const hash = await sha256Hex(file);
        return { ok: true, file, hash };
      })
    );

    // Collect validation failures into a single combined toast (TOAST_LIMIT
    // is 1 so firing them in a loop just shows the last one).
    const validationErrors: string[] = [];
    const seenHashesThisBatch = new Set<string>();
    const existingHashes = new Set(existing.map((m) => m.hash).filter(Boolean) as string[]);
    const toUpload: Array<{ file: File; hash: string }> = [];

    for (const item of prepared) {
      if (!item.ok) {
        validationErrors.push(item.error);
        continue;
      }
      // Hash-based dedup catches identical content even with different
      // filenames. Fall back to name+size for any existing item that
      // doesn't have a hash (e.g., older saved state).
      const matchedByHash = existingHashes.has(item.hash) || seenHashesThisBatch.has(item.hash);
      const matchedByMeta = existing.some(
        (m) => m.type === 'file' && !m.hash && m.name === item.file.name && m.size === item.file.size
      );
      if (matchedByHash || matchedByMeta) {
        validationErrors.push(`${item.file.name} is already in the list`);
        continue;
      }
      seenHashesThisBatch.add(item.hash);
      toUpload.push({ file: item.file, hash: item.hash });
    }

    if (validationErrors.length > 0) {
      toast({
        title: validationErrors.length === 1 ? "Upload skipped" : `${validationErrors.length} files skipped`,
        description: validationErrors.join('\n'),
      });
    }

    if (toUpload.length === 0) {
      setUploading(false);
      return;
    }

    // Run all uploads concurrently. Use allSettled so one failure doesn't
    // wipe out the other results.
    const results = await Promise.allSettled(
      toUpload.map(({ file, hash }) =>
        uploadFile(file, idPrefix).then((item) => ({ ...item, hash }))
      )
    );

    const successItems: MaterialItem[] = [];
    const uploadErrors: string[] = [];
    results.forEach((result, idx) => {
      if (result.status === 'fulfilled') {
        successItems.push(result.value);
      } else {
        const err = result.reason;
        const msg = err instanceof Error ? err.message : 'Unknown error';
        uploadErrors.push(
          msg.startsWith(`${toUpload[idx].file.name} `) ? msg : `${toUpload[idx].file.name}: ${msg}`
        );
      }
    });

    if (uploadErrors.length > 0) {
      toast({
        title: uploadErrors.length === 1 ? "Upload failed" : `${uploadErrors.length} uploads failed`,
        description: uploadErrors.join('\n'),
        duration: 9000,
      });
    }

    setUploading(false);

    if (successItems.length > 0) {
      setItems(prev => [...prev, ...successItems]);
      const truncated = successItems.filter(item => item.truncated);
      const baseDescription = `${successItems.length} file${successItems.length === 1 ? '' : 's'} ready to use.`;
      const truncatedNote = truncated.length === 0
        ? ""
        : truncated.length === 1
        ? ` Heads up: "${truncated[0].name}" was shortened — a ${MAX_EXTRACTED_CHARS.toLocaleString()}-character sample from across the document will be sent to the AI (${truncated[0].originalLength?.toLocaleString() ?? '?'} characters total).`
        : ` Heads up: ${truncated.length} files exceeded ${MAX_EXTRACTED_CHARS.toLocaleString()} chars and were shortened to samples from across each document. Hover the chips to see which.`;
      toast({
        title: "Files added",
        description: baseDescription + truncatedNote,
        // Truncation warnings have a longer message and matter for output
        // quality — give the user time to actually read them.
        duration: truncated.length > 0 ? 9000 : undefined,
      });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingMaterials(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesUpload(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDraggingMaterials) setIsDraggingMaterials(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingMaterials(false);
  };

  // Persist the current form into TestConfig (used by both Next and the
  // "Regenerate Blueprint" path so they stay in sync).
  const saveCurrentConfig = () => {
    // Blank is allowed — fall back to the default name shown in the placeholder.
    setTestName(localTestName.trim() || "Untitled Test");

    const customSkills: Array<{ id: string; name: string; content: string }> = [];

    for (const material of materials) {
      // A url's extracted page text lives in segments (content is the link).
      const urlText = material.segments?.map((s) => s.text).filter(Boolean).join('\n\n') ?? '';
      customSkills.push({
        id: material.id,
        name: `reference-material-${material.type}`,
        content: material.type === 'text'
          ? material.content
          : material.type === 'file'
          ? `Reference material from file "${material.name}":\n\n${material.content}`
          : urlText
          ? `Reference material from link ${material.name}:\n\n${urlText}`
          : `Reference material URL: ${material.name} (${material.content})`,
      });
    }

    // Push the Additional Context as a named skill so Blacksmith preloads it
    // into the question-generator's context rather than treating it as inline
    // prompt text. The named skill keeps it addressable and separates it from
    // the source-material skills above.
    const extraContext = additionalContext.trim();
    if (extraContext) {
      customSkills.push({
        id: 'teacher-guidance',
        name: 'teacher-guidance',
        content:
          "Teacher-provided direction for how to shape this test. NOT source material — do not generate questions directly from this text. Use it to guide what to emphasize, the level of difficulty, pacing, and any coverage or tone notes.\n\n" +
          extraContext,
      });
    }

    const testConfig: TestConfig = {
      audience,
      // Persist empty if the user hasn't picked yet — don't coerce to a
      // hidden default. The Next button blocks on these being set, and
      // downstream BuildBlueprint has its own grade-7 fallback for safety.
      readingLevel: readingLevel as TestConfig["readingLevel"],
      subject: (subject || undefined) as TestConfig["subject"],
      testLengthType: "items",
      testLengthValue: lengthValue,
      materials: materials.map((material) => ({
        id: material.id,
        type: material.type,
        name: material.name,
        url: material.content,
        content: material.content,
        size: material.size,
        mimetype: material.mimeType,
        truncated: material.truncated,
        originalLength: material.originalLength,
        hash: material.hash,
        segments: material.segments,
        uploadId: material.uploadId,
        // Don't persist the transient 'checking' state — that should
        // force a fresh probe on the next mount.
        urlStatus: material.urlStatus === 'checking' ? undefined : material.urlStatus,
        urlStatusCheckedAt: material.urlStatus === 'checking' ? undefined : material.urlStatusCheckedAt,
      })),
      lessonPlans: [],
      additionalContext: additionalContext.trim() || undefined,
      customSkills: customSkills.length > 0 ? customSkills : undefined,
    };
    setConfig(testConfig);
  };

  // Persist on every change so uploads / typed input survive a page refresh.
  // TestContext debounces its own localStorage write by 1s, so this fires
  // freely as the user edits without thrashing storage. Effect intentionally
  // depends on saveCurrentConfig closure values via the listed state items.
  useEffect(() => {
    saveCurrentConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials, additionalContext, audience, readingLevel, subject, lengthValue, localTestName]);

  // Materials are added through several paths (file upload, text, URL),
  // so clear the missing-materials error centrally whenever the count
  // changes rather than threading clearError through each handler.
  useEffect(() => {
    if (materials.length > 0) clearError('materials');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials.length]);

  // Re-probe URL materials on mount only when the cached result is
  // missing or stale (older than URL_STATUS_TTL_MS). Fresh cards skip
  // the probe entirely so refreshes are fast.
  useEffect(() => {
    for (const m of materials) {
      if (m.type === 'url' && m.urlStatus !== 'unsupported_provider' && !isUrlStatusFresh(m)) {
        setMaterials((prev) => prev.map((x) => (x.id === m.id ? { ...x, urlStatus: 'checking' } : x)));
        void checkUrlAccessibility(m.id, m.content);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-time migration: prior versions auto-saved Reading Level = "10" and
  // Subject Area = "math" as defaults, so returning users would see those
  // pre-filled even though they're now meant to be empty. Clear once per
  // user and never again.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(REQUIRED_RESET_KEY) === '1') return;
    setReadingLevel('');
    setSubject('');
    try { localStorage.setItem(REQUIRED_RESET_KEY, '1'); } catch { /* private mode */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A material counts as "usable" if it'll actually contribute content the
  // AI can read. Files and text must have non-empty content (a file that
  // extracted to nothing must never satisfy the materials-required gate —
  // backstop for anything that slips past the upload-time rejection). URLs
  // only qualify if the server's accessibility probe didn't flag them as
  // login-required or unreachable; URLs still checking or with no status yet
  // are given the benefit of the doubt (most URLs come back public).
  const isUsableMaterial = (m: MaterialItem) => {
    if (m.type === 'url') {
      return m.urlStatus !== 'login_required' && m.urlStatus !== 'unreachable';
    }
    const hasReadableContent =
      (typeof m.content === 'string' && m.content.trim().length > 0) ||
      (Array.isArray(m.segments) && m.segments.some((s) => typeof s.text === 'string' && s.text.trim().length > 0));
    return hasReadableContent;
  };

  const handleNext = () => {
    const missing: string[] = [];
    // Name is optional — blank defaults to "Untitled Test" (the placeholder).
    if (!readingLevel) missing.push('readingLevel');
    if (!subject) missing.push('subject');
    if (!materials.some(isUsableMaterial)) missing.push('materials');

    if (missing.length > 0) {
      setMissingFields(new Set(missing));
      // Scroll the first missing field into the center of the viewport.
      const refMap: Record<string, React.RefObject<HTMLDivElement>> = {
        name: nameWrapRef,
        readingLevel: readingLevelWrapRef,
        subject: subjectWrapRef,
        materials: materialsWrapRef,
      };
      refMap[missing[0]]?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setMissingFields(new Set());
    saveCurrentConfig();
    setLocation("/create/blueprint");
  };

  const handleRegenerateBlueprint = () => {
    saveCurrentConfig();
    // Clearing the blueprint causes BuildBlueprint to refetch on mount.
    setBlueprint(null);
    setLocation("/create/blueprint");
  };

  const handleBack = () => {
    setLocation("/dashboard");
  };

  return (
    <div className="min-h-screen bg-background">
      <Header pageTitle="Create Test" />

      <main className="container mx-auto px-6 py-12 max-w-5xl">
        <ProgressIndicator currentStep={1} />

        {/* When the missing-fields message is showing, tighten the gap below
            the card so the message sits equidistant from the card above and
            the button row below (matches the gap-3 used inside the right
            column). Otherwise keep the normal mb-8 page rhythm. */}
        <Card className={missingFields.size > 0 ? 'mb-3' : 'mb-8'}>
          <CardHeader>
            <CardTitle className="text-3xl">Let's Make Your Test!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            <div className="space-y-2" ref={nameWrapRef}>
              <Label htmlFor="testName" className="text-base font-semibold">
                Name your test
              </Label>
              <Input
                id="testName"
                type="text"
                value={localTestName}
                onChange={(e) => { setLocalTestName(e.target.value); clearError('name'); }}
                placeholder="Untitled Test"
                className={`w-full h-12 text-base placeholder:text-muted-foreground/60 placeholder:italic ${
                  missingFields.has('name') ? 'border-red-500 focus-visible:ring-red-500' : ''
                }`}
                data-testid="input-test-name"
              />
            </div>

            {/* Reading Level + Subject Area pair side-by-side on tablet/
                desktop; stack on mobile. Each cell is a flex column that
                pushes its label to the top and its input to the bottom.
                Grid cells stretch to the row height by default, so when
                the Reading Level label wraps to two lines, the Subject
                Area cell stretches with it — both labels start at the
                top, both inputs sit on the same baseline at the bottom. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2 md:justify-between" ref={readingLevelWrapRef}>
                <Label
                  htmlFor="reading-level"
                  className={`text-base font-semibold ${missingFields.has('readingLevel') ? 'text-red-600' : ''}`}
                >
                  At what reading level would you like items written? <span className="text-muted-foreground font-normal">(required)</span>
                </Label>
                <Select
                  value={readingLevel}
                  onValueChange={(value) => { setReadingLevel(value as TestConfig["readingLevel"]); clearError('readingLevel'); }}
                >
                  <SelectTrigger
                    id="reading-level"
                    data-testid="select-reading-level"
                    className={missingFields.has('readingLevel') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                  >
                    <SelectValue placeholder="Select reading level" />
                  </SelectTrigger>
                  <SelectContent>
                    {["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "Adult / workforce", "College", "Graduate"].map((level) => {
                      const isAdult = level === "Adult / workforce";
                      const helpText = "Adults outside a college setting, such as job or certification candidates.";
                      const item = (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      );
                      if (!isAdult) return item;
                      return (
                        <Tooltip key={level} delayDuration={300}>
                          <TooltipTrigger asChild>
                            {item}
                          </TooltipTrigger>
                          <TooltipContent data-testid="reading-level-tooltip" side="right" align="center">
                            <p className="max-w-[240px] text-xs">{helpText}</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <SubjectSelector
                ref={subjectWrapRef}
                className="flex flex-col gap-2 md:justify-between"
                value={subject}
                onChange={(v) => { setSubject(v); clearError('subject'); }}
                hasError={missingFields.has('subject')}
              />
            </div>

            <div className="space-y-4">
              <Label htmlFor="items" className="text-base font-semibold">Approximately how long would you like your test?</Label>
              <div className="flex items-center gap-4">
                <span>Items</span>
                <Input
                  id="items"
                  type="number"
                  min="1"
                  max="20"
                  value={lengthValue}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    if (Number.isNaN(raw)) return;
                    setLengthValue(Math.max(1, Math.min(20, Math.floor(raw))));
                  }}
                  className="w-24"
                  data-testid="input-items"
                />
                <span className="text-sm text-muted-foreground">(max 20)</span>
              </div>
            </div>

            <div className="space-y-4" ref={materialsWrapRef}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className={`text-base font-semibold ${missingFields.has('materials') ? 'text-red-600' : ''}`}>
                  Add Lesson Materials <span className="text-muted-foreground font-normal">(required)</span>
                </Label>
                <ToggleGroup type="single" value={materialType} onValueChange={(value) => value && setMaterialType(value as "file" | "text" | "url")} className="border rounded-md">
                  <ToggleGroupItem value="file" aria-label="Upload File" className="px-3 py-2">
                    <Upload className="w-4 h-4 mr-2" />
                    File
                  </ToggleGroupItem>
                  <ToggleGroupItem value="text" aria-label="Enter Text" className="px-3 py-2">
                    <Type className="w-4 h-4 mr-2" />
                    Text
                  </ToggleGroupItem>
                  <ToggleGroupItem value="url" aria-label="Add URL" className="px-3 py-2">
                    <LinkIcon className="w-4 h-4 mr-2" />
                    URL
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>

              <div className="relative">
                {showSourceBanner && (
                  <div
                    className="absolute inset-0 z-10 rounded-md border-2 border-amber-400 bg-amber-50 text-amber-900 p-4 flex flex-col"
                    data-testid="banner-source-materials"
                  >
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
                      <div className="flex-1 text-[15px] leading-relaxed">
                        <p className="font-semibold mb-1">What are lesson materials?</p>
                        <p>
                          Upload the files you want this test to be built from — readings, slides, textbook
                          chapters, study guides. <span className="font-medium">Questions are generated directly from
                          what you upload here</span>, so the more closely your materials match what you taught, the
                          better the test will match your class.
                        </p>
                      </div>
                    </div>
                    <div className="mt-auto pt-3 flex justify-center">
                      <Button
                        size="lg"
                        onClick={dismissSourceBanner}
                        data-testid="button-dismiss-source-banner"
                        className="font-bold text-base px-6"
                      >
                        Close this to upload materials
                      </Button>
                    </div>
                  </div>
                )}
                <div className={`flex flex-col ${materialType === "url" ? "" : "h-[160px]"}`}>
                {materialType === "file" && (
                  <>
                    <input
                      ref={materialFileInputRef}
                      type="file"
                      multiple
                      accept={ACCEPT_ATTR}
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files) handleFilesUpload(e.target.files);
                        e.target.value = ""; // allow re-selecting the same file
                      }}
                      data-testid="file-upload-input"
                    />
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label="Upload materials file"
                      onClick={() => !isUploadingMaterials && materialFileInputRef.current?.click()}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && !isUploadingMaterials) {
                          e.preventDefault();
                          materialFileInputRef.current?.click();
                        }
                      }}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors h-full flex flex-col justify-center ${
                        isUploadingMaterials
                          ? "border-secondary bg-secondary/10 cursor-wait"
                          : isDraggingMaterials
                          ? "border-secondary bg-secondary/10 cursor-copy"
                          : missingFields.has('materials')
                          ? "border-red-500 hover:border-red-600 cursor-pointer"
                          : "border-border hover:border-secondary hover:bg-secondary/5 cursor-pointer"
                      }`}
                      data-testid="dropzone-materials"
                    >
                      {isUploadingMaterials ? (
                        <>
                          <Loader2 className="w-8 h-8 text-secondary mx-auto mb-2 animate-spin" />
                          <p className="text-sm text-muted-foreground">Uploading and extracting text…</p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-secondary mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">Click to upload or drag and drop</p>
<p className="text-xs text-muted-foreground mt-1">PDF, DOCX, TXT, PPTX (max 25 MB)</p>
                        </>
                      )}
                    </div>
                  </>
                )}
                
                {materialType === "text" && (
                  <div className="h-full flex flex-col">
                    <Textarea
                      placeholder="Enter or paste your text content here..."
                      value={textInput}
                      onChange={(e) => setTextInput(e.target.value)}
                      onKeyDown={(e) => {
                        // Ctrl/Cmd+Enter adds the text; plain Enter still
                        // inserts a newline.
                        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                          e.preventDefault();
                          handleAddText();
                        }
                      }}
                      className={`flex-1 min-h-0 resize-none ${
                        missingFields.has('materials') ? 'border-red-500 focus-visible:ring-red-500' : ''
                      }`}
                      data-testid="input-text"
                    />
                    <div className="flex justify-end mt-2">
                      <Button onClick={handleAddText} variant="secondary" data-testid="button-add-text">
                        Add Text
                      </Button>
                    </div>
                  </div>
                )}

                {materialType === "url" && (
                  <div className="flex gap-2 w-full">
                    <div className="flex-1 relative">
                      <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Enter website URL"
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAddUrl()}
                        className={`pl-10 ${
                          missingFields.has('materials') ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }`}
                        data-testid="input-url"
                      />
                    </div>
                    <Button onClick={handleAddUrl} variant="secondary" data-testid="button-add-url">
                      Add URL
                    </Button>
                  </div>
                )}
                </div>
              </div>

              {/* Uploaded materials — preview cards. Each ~160px wide x 120px
                  tall (matching the Additional Context textarea height) so
                  filenames/excerpts/site labels are readable at a glance.
                  Files show their name (multi-line with ellipsis); text
                  materials show their actual content (truncated); URLs show
                  the hostname prominently with the full link below. */}
              {materials.length > 0 && (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3 pt-2">
                  {materials.map((material) => (
                    <div
                      key={material.id}
                      className="relative group"
                      data-testid="material-card"
                    >
                      {(() => {
                        const urlWarning =
                          material.type === 'url' &&
                          (material.urlStatus === 'login_required' || material.urlStatus === 'unreachable');
                        const googleWarning =
                          material.type === 'url' && material.urlStatus === 'unsupported_provider';
                        const cardBg = urlWarning
                          ? 'bg-red-50 border-red-300 group-hover:bg-red-100'
                          : googleWarning || material.truncated
                          ? 'bg-amber-50 border-amber-200'
                          : 'bg-blue-50 border-blue-200 group-hover:bg-blue-100';
                        return (
                          <div
                            onClick={() => {
                              if (material.type === 'file') setPreviewMaterial(material);
                            }}
                            role={material.type === 'file' ? 'button' : undefined}
                            tabIndex={material.type === 'file' ? 0 : undefined}
                            onKeyDown={(e) => {
                              if (material.type !== 'file') return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setPreviewMaterial(material);
                              }
                            }}
                            className={`h-[120px] rounded-md border p-3 overflow-hidden flex flex-col transition-colors ${
                              material.type === 'file' ? 'cursor-pointer' : ''
                            } ${cardBg}`}
                          >
                        <div className="flex items-center justify-between mb-1.5 shrink-0">
                          <span className={`text-[11px] font-semibold uppercase tracking-wide ${urlWarning ? 'text-red-700' : 'text-muted-foreground'}`}>
                            {material.type === 'file'
                              ? fileTypeLabel(material.name)
                              : material.type === 'text'
                              ? 'Text'
                              : 'Link'}
                          </span>
                          {material.truncated && (
                            <span className="text-[11px] font-medium text-amber-700" title={`A ${MAX_EXTRACTED_CHARS.toLocaleString()}-character sample from across the document was sent to the AI (${material.originalLength?.toLocaleString() ?? '?'} characters total).`}>
                              Shortened
                            </span>
                          )}
                          {urlWarning && (
                            <span className="text-[11px] font-semibold uppercase text-red-700" aria-label="Warning">
                              Warning
                            </span>
                          )}
                          {googleWarning && (
                            <span className="text-[11px] font-semibold uppercase text-amber-700" aria-label="Warning">
                              Warning
                            </span>
                          )}
                        </div>

                        {material.type === 'file' && (
                          <p className="text-[13px] leading-snug wrap-break-word line-clamp-5 overflow-hidden flex-1">
                            {material.name}
                          </p>
                        )}

                        {material.type === 'text' && (
                          <p className="text-[13px] leading-snug whitespace-pre-wrap wrap-break-word line-clamp-5 overflow-hidden flex-1">
                            {material.content || material.name}
                          </p>
                        )}

                        {material.type === 'url' && (
                          <>
                            <a
                              href={material.content || material.name}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-[13px] font-semibold leading-snug wrap-break-word line-clamp-2 mb-1 text-blue-700 hover:text-blue-900 underline"
                            >
                              {urlSiteLabel(material.content || material.name)}
                            </a>
                            {urlWarning ? (
                              <p className="text-[11px] leading-snug text-red-700 flex-1 overflow-hidden">
                                {material.urlStatus === 'login_required'
                                  ? 'Requires login — the AI likely won’t be able to access this content.'
                                  : 'This link doesn’t seem to work.'}
                              </p>
                            ) : googleWarning ? (
                              <p className="text-[11px] leading-snug text-amber-700 flex-1 overflow-hidden">
                                Google Slides/Docs links usually aren’t readable for the blueprint.
                              </p>
                            ) : (
                              <p className="text-[11px] text-muted-foreground leading-snug break-all line-clamp-3 overflow-hidden flex-1">
                                {material.content || material.name}
                              </p>
                            )}
                          </>
                        )}
                          </div>
                        );
                      })()}

                      <button
                        onClick={() => handleRemoveMaterial(material.id)}
                        aria-label="Remove material"
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      >
                        <X className="w-3 h-3" />
                      </button>

                      {material.type === 'file' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setPreviewMaterial(material); }}
                          aria-label="Preview or edit material"
                          className="absolute bottom-1.5 right-1.5 z-10 flex items-center gap-1 rounded-md bg-white/85 hover:bg-white border border-border px-1.5 py-0.5 text-[10px] font-medium text-foreground shadow-xs"
                          data-testid="button-preview-material"
                        >
                          Preview / Edit <Eye className="w-3 h-3" />
                        </button>
                      )}

                      {material.type === 'text' && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <button
                              className="absolute bottom-1.5 right-1.5 z-10 flex items-center gap-1 rounded-md bg-white/85 hover:bg-white border border-border px-1.5 py-0.5 text-[10px] font-medium text-foreground shadow-xs"
                              data-testid="button-preview-material"
                            >
                              Preview / Edit <Eye className="w-3 h-3" />
                            </button>
                          </DialogTrigger>
                          <DialogContent className="max-w-3xl">
                            <DialogHeader>
                              <DialogTitle>Edit Text Content</DialogTitle>
                            </DialogHeader>
                            <Textarea
                              value={editingText?.id === material.id ? editingText.content : material.content}
                              onChange={(e) => setEditingText({ id: material.id, content: e.target.value, isMaterial: true })}
                              className="min-h-[55vh]"
                            />
                            <div className="flex justify-end gap-2">
                              <DialogClose asChild>
                                <Button variant="outline" onClick={() => setEditingText(null)}>Cancel</Button>
                              </DialogClose>
                              <DialogClose asChild>
                                <Button onClick={() => handleEditMaterial(material.id, editingText?.content || material.content)}>Save</Button>
                              </DialogClose>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}

                      {material.type === 'url' && material.segments && material.segments.length > 0 && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <button
                              className="absolute bottom-1.5 right-1.5 z-10 flex items-center gap-1 rounded-md bg-white/85 hover:bg-white border border-border px-1.5 py-0.5 text-[10px] font-medium text-foreground shadow-xs"
                              data-testid="button-preview-material"
                            >
                              Preview / Edit <Eye className="w-3 h-3" />
                            </button>
                          </DialogTrigger>
                          <DialogContent className="max-w-3xl">
                            <DialogHeader>
                              <DialogTitle className="break-all">Text read from {material.name}</DialogTitle>
                            </DialogHeader>
                            <p className="text-xs text-muted-foreground">
                              This is the text we pulled from the link and send to the AI. Editing it changes what the AI sees — it doesn't change the linked page.
                            </p>
                            <Textarea
                              value={editingText?.id === material.id ? editingText.content : (material.segments?.map((s) => s.text).filter(Boolean).join('\n\n') ?? '')}
                              onChange={(e) => setEditingText({ id: material.id, content: e.target.value, isMaterial: true })}
                              className="min-h-[55vh] font-mono text-xs leading-relaxed"
                            />
                            <div className="flex justify-end gap-2">
                              <DialogClose asChild>
                                <Button variant="outline" onClick={() => setEditingText(null)}>Cancel</Button>
                              </DialogClose>
                              <DialogClose asChild>
                                <Button onClick={() => handleEditUrlContent(material.id, editingText?.id === material.id ? editingText.content : (material.segments?.map((s) => s.text).filter(Boolean).join('\n\n') ?? ''))}>Save</Button>
                              </DialogClose>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Additional Context — a single optional free-text field that
                replaces the old separate "Lesson Plan / Learning Objectives"
                uploader. Passed to the AI as instructions/constraints,
                NOT as source material. */}
            <div className="space-y-2">
              <Label htmlFor="additional-context" className="text-base font-semibold">
                Additional Context <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <p className="text-sm text-muted-foreground">
                The AI creates better questions when it has more context and direction. The lesson plan provides
                core material, but you can add extra details to help the AI tailor this assessment to your needs.
              </p>
              <Textarea
                id="additional-context"
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                placeholder="Example: Focus on the causes of the American Revolution and include more critical thinking questions instead of memorization."
                className="min-h-[120px] placeholder:italic"
                data-testid="textarea-additional-context"
              />
            </div>

          </CardContent>
        </Card>

        {/* Next is always pressable. If the user clicks with empty required
            fields we set missingFields, mark each field red, scroll to the
            first one, and surface a persistent error message above the
            button so they know what's missing even after scrolling back. */}
        {(() => {
          const hasMaterials = materials.length > 0;
          const baseDisabled = isUploadingMaterials;
          // If the user has materials but all of them are flagged red (login
          // required / unreachable), swap the materials label so the message
          // points at the right problem instead of suggesting they add a new
          // material when they have several.
          const allMaterialsInvalid = materials.length > 0 && !materials.some(isUsableMaterial);
          const missingList = Array.from(missingFields).map((f) => {
            if (f === 'materials' && allMaterialsInvalid) {
              return 'at least one accessible lesson material';
            }
            return fieldLabels[f] ?? f;
          });
          // Use Intl.ListFormat so 2 items join with "and", 3+ items get
          // an Oxford comma. Avoids the previous comma-only join that read
          // as "X, Y" instead of "X and Y".
          const joined = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(missingList);
          return (
            // items-end keeps the Back button aligned with the Next row at
            // the bottom of the flex parent so it doesn't stretch vertically
            // when the missing-fields message above Next pushes the right
            // column's height up.
            <div className="flex justify-between items-end">
              <Button variant="outline" onClick={handleBack} data-testid="button-back">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex flex-col items-end gap-3">
                {missingList.length > 0 && (
                  <p
                    className="text-base text-red-600 font-medium text-right"
                    role="alert"
                    aria-live="polite"
                    data-testid="text-next-error"
                  >
                    Please add {joined} before continuing.
                  </p>
                )}
                <div className="flex gap-3">
                  {blueprint && hasMaterials && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="secondary"
                          disabled={baseDisabled}
                          data-testid="button-regenerate-blueprint"
                        >
                          Regenerate Blueprint
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Regenerate the blueprint?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will discard the existing blueprint (including any edits to topics or per-standard distributions) and generate a fresh one based on your current settings.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={handleRegenerateBlueprint}>
                            Regenerate
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <Button
                    onClick={handleNext}
                    disabled={baseDisabled}
                    data-testid="button-next"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}
      </main>

      <MaterialPreviewDialog
        open={previewMaterial !== null}
        onOpenChange={(open) => { if (!open) setPreviewMaterial(null); }}
        uploadId={previewMaterial?.uploadId}
        fileName={previewMaterial?.name ?? ''}
        mimeType={previewMaterial?.mimeType}
        extractedText={previewMaterial?.content ?? ''}
        segments={previewMaterial?.segments}
        onSaveExtractedText={(newText) => {
          if (!previewMaterial) return;
          // Replace the material's content and collapse segments to a single
          // anchorless block (free-form edits invalidate page/slide bounds).
          setMaterials((prev) =>
            prev.map((m) =>
              m.id === previewMaterial.id
                ? { ...m, content: newText, segments: newText ? [{ text: newText }] : [] }
                : m,
            ),
          );
          // Keep the open dialog in sync with the new content.
          setPreviewMaterial({
            ...previewMaterial,
            content: newText,
            segments: newText ? [{ text: newText }] : [],
          });
          toast({
            title: 'Saved',
            description: 'Updated extracted text for this material.',
          });
        }}
      />
    </div>
  );
}
